/*
 * Renders the Route Network map into a portal element instead of an
 * <iframe src="route-map.html">, and instead of directly inside the
 * <svg> reserved for it in snippets/sat-routes.liquid.
 *
 * Why not the iframe: Shopify serves every raw .html theme asset with a
 * Content-Security-Policy: sandbox header (confirmed live: fetching
 * assets/route-map.html directly returned "content-security-policy:
 * sandbox allow-scripts allow-forms ..." with no allow-same-origin).
 * That's spec-legal and harmless for a *top-level* load of that file —
 * sandbox has no effect outside a nested browsing context, which is why
 * testing it standalone looked fine — but framed (exactly how it was
 * used), the sandbox is enforced and the framed document gets an opaque
 * origin, breaking its same-origin fetch('/pages/sat-live-status')
 * outright and, in this environment, its very first paint too.
 *
 * Why not draw straight into the <svg> reserved in sat-routes.liquid:
 * confirmed live that this actually is worse than it first looked. That
 * <svg> sits inside the app's own vdom-rendered markup, and this runtime
 * reconciles every managed element's children against its own (empty, for
 * this element) vnode on every render — which happens very often (the
 * Departures board wave alone ticks every ~3.5s). d3.append()'d content
 * was confirmed being wiped back to zero children within a few seconds of
 * being drawn, every time, regardless of any init-once flag on the
 * element — the element survives, its children don't. Re-running init
 * inside the wrapped renderVals doesn't fix this either: renderVals runs
 * *before* the framework's own DOM patch for that same cycle, so anything
 * appended there gets wiped by that same cycle's patch a moment later.
 *
 * The fix: draw into a plain <svg> created here and appended directly to
 * document.body — a node the app's vdom has never heard of and will never
 * touch. A lightweight interval keeps that portal's position, size and
 * visibility synced to the real <svg data-m="route-map-svg"> placeholder
 * still declared in sat-routes.liquid (which stays empty and untouched,
 * just a reserved rectangle in the flow), so it tracks scrolling, resizes,
 * and the section being shown/hidden without needing any hook into the
 * app's own render cycle at all.
 */
(function () {
  'use strict';

  var FALLBACK_CITIES = [
    {code:'MIA', name:'Miami, FL',        lat:25.79, lon:-80.29, hub:true},
    {code:'ORF', name:'Norfolk, VA',      lat:36.85, lon:-76.29},
    {code:'SAN', name:'San Diego, CA',    lat:32.72, lon:-117.16},
    {code:'COS', name:'Colorado Springs, CO', lat:38.83, lon:-104.82},
    {code:'MMH', name:'Mammoth Lakes, CA',lat:37.65, lon:-118.97},
    {code:'FAY', name:'Fayetteville, NC', lat:35.05, lon:-78.88},
    {code:'ELP', name:'El Paso, TX',      lat:31.76, lon:-106.49},
    {code:'DEN', name:'Denver, CO',       lat:39.74, lon:-104.99},
    {code:'SEA', name:'Seattle, WA',      lat:47.61, lon:-122.33},
    {code:'PIT', name:'Pittsburgh, PA',   lat:40.44, lon:-79.99},
    {code:'NOL', name:'New Orleans, LA',  lat:29.95, lon:-90.07}
  ];
  var FALLBACK_ROUTES = [['MIA','FAY'],['FAY','ORF'],['ORF','PIT'],['PIT','DEN'],['DEN','COS'],['SAN','MMH'],['SAN','COS'],['MIA','MMH'],['SAN','ELP'],['ELP','DEN'],['SEA','DEN'],['MIA','NOL'],['NOL','ELP']];

  var HUB_CITY = 'Mammoth Lakes, CA';

  var CITY_COORDS = {
    'miami, fl': [25.7617, -80.1918], 'tampa, fl': [27.9506, -82.4572],
    'jacksonville, fl': [30.3322, -81.6557], 'key west, fl': [24.5551, -81.7800],
    'homestead, fl': [25.4687, -80.4776], 'norfolk, va': [36.8508, -76.2859],
    'chesapeake, va': [36.7682, -76.2875], 'suffolk, va': [36.7282, -76.5836],
    'portsmouth, va': [36.8354, -76.2983], 'newport news, va': [37.0871, -76.4730],
    'san diego, ca': [32.7157, -117.1611], 'el centro, ca': [32.7920, -115.5631],
    'coronado, ca': [32.6859, -117.1831], 'pendleton, ca': [33.3852, -117.3592],
    'twentynine palms, ca': [34.1355, -116.0542], 'colorado springs, co': [38.8339, -104.8214],
    'pueblo, co': [38.2544, -104.6091], 'schriever, co': [38.8036, -104.5253],
    'cheyenne mtn, co': [38.7442, -104.8462], 'cheyenne mountain, co': [38.7442, -104.8462],
    'peterson afb, co': [38.8161, -104.6997], 'mammoth lakes, ca': [37.6485, -118.9721],
    'fresno, ca': [36.7378, -119.7871], 'ridgecrest, ca': [35.6225, -117.6709],
    'china lake, ca': [35.6866, -117.6900], 'bishop, ca': [37.3639, -118.3952],
    'fayetteville, nc': [35.0527, -78.8784], 'jacksonville, nc': [34.7541, -77.4302],
    'goldsboro, nc': [35.3849, -77.9928], 'wilmington, nc': [34.2257, -77.9447],
    'fort bragg, nc': [35.1391, -79.0060], 'bozeman, mt': [45.6770, -111.0429],
    'johnsonville, sc': [33.8140, -79.4478], 'belgrade, mt': [45.7752, -111.1832],
    'fayetteville, tn': [35.1509, -86.5717], 'darien, ct': [41.0784, -73.4690],
    'tucson, az': [32.2226, -110.9747], 'el paso, tx': [31.7619, -106.4850],
    'denver, co': [39.7392, -104.9903], 'seattle, wa': [47.6062, -122.3321],
    'pittsburgh, pa': [40.4406, -79.9959], 'new orleans, la': [29.9511, -90.0715],
    'bismarck, nd': [46.8083, -100.7837], 'rindge, nh': [42.7314, -72.1810],
    'fort mitchell, ky': [39.0470, -84.5827], 'columbus junction, ia': [41.2833, -91.3660],
    'oviedo, fl': [28.6700, -81.2081], 'seguin, tx': [29.5688, -97.9647],
    'caddo mills, tx': [33.0587, -96.2286], 'dulles, va': [38.9550, -77.4472],
    'justin, tx': [33.0837, -97.2986], 'tifton, ga': [31.4505, -83.5085],
    'boone, nc': [36.2168, -81.6746], 'mechanicsville, md': [38.4526, -76.7397],
    'boerne, tx': [29.7947, -98.7320], 'college station, tx': [30.6280, -96.3344],
    'lufkin, tx': [31.3382, -94.7291], 'hendersonville, nc': [35.3187, -82.4610],
    'vacaville, ca': [38.3566, -121.9877], 'wyoming, mi': [42.9134, -85.7053],
    'broadview heights, oh': [41.3159, -81.6790], 'san antonio, tx': [29.4241, -98.4936],
    'shirley, ny': [40.7940, -72.8593], 'saint john, in': [41.4467, -87.4703]
  };

  function normCity(s) { return String(s || '').trim().toLowerCase(); }
  function coordsFor(cityName) {
    var c = CITY_COORDS[normCity(cityName)];
    return c ? { lat: c[0], lon: c[1] } : null;
  }

  var portal = null;      // the real, always-preserved <svg>, outside app control
  var placeholder = null; // the empty anchor <svg> the app renders/manages
  var mapReady = false;

  function ensurePortal() {
    if (portal) return portal;
    portal = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    portal.setAttribute('data-m', 'route-map-portal');
    portal.style.position = 'fixed';
    portal.style.pointerEvents = 'none';
    portal.style.background = '#E8DCBE';
    portal.style.display = 'none';
    portal.style.zIndex = '1';
    document.body.appendChild(portal);
    return portal;
  }

  function buildMap(svgEl) {
    if (!window.d3 || !window.topojson) return false;
    mapReady = true;

    var svg = d3.select(svgEl);
    var W = 966, H = 420; // reserved panel size — synced visually via syncPortal below
    svg.attr('viewBox', '0 0 ' + W + ' ' + H);

    var projection = d3.geoAlbersUsa().fitSize([W * 0.94, H * 0.9], { type: 'Sphere' });
    projection.translate([W / 2, H / 2]);
    var path = d3.geoPath(projection);

    var g = svg.append('g');
    g.append('path').datum(d3.geoGraticule()()).attr('d', path)
      .attr('fill', 'none').attr('stroke', 'rgba(107,74,46,.14)').attr('stroke-width', 0.6);

    function svgPointAt(d, t) {
      var tmp = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      tmp.setAttribute('d', d);
      var len = tmp.getTotalLength();
      return len ? tmp.getPointAtLength(len * t) : { x: 0, y: 0 };
    }

    var routesLayer = null, citiesLayer = null;

    function plot(cities, routePairs) {
      if (routesLayer) routesLayer.remove();
      if (citiesLayer) citiesLayer.remove();
      routesLayer = g.append('g');
      citiesLayer = g.append('g');

      var byCode = {};
      cities.forEach(function (c) { byCode[c.code] = c; });

      routePairs.forEach(function (pair) {
        var ca = byCode[pair[0]], cb = byCode[pair[1]];
        if (!ca || !cb) return;
        var pa = projection([ca.lon, ca.lat]), pb = projection([cb.lon, cb.lat]);
        if (!pa || !pb) return;
        var mx = (pa[0] + pb[0]) / 2, my = (pa[1] + pb[1]) / 2 - 40;
        var line = 'M' + pa[0] + ',' + pa[1] + ' Q' + mx + ',' + my + ' ' + pb[0] + ',' + pb[1];
        routesLayer.append('path').attr('d', line).attr('fill', 'none')
          .attr('stroke', '#8A3521').attr('stroke-width', 1.3).attr('stroke-dasharray', '5 5')
          .append('animate').attr('attributeName', 'stroke-dashoffset').attr('from', 0).attr('to', -20).attr('dur', '1.6s').attr('repeatCount', 'indefinite');
        var blip = routesLayer.append('circle').attr('r', 2.8).attr('fill', '#A6402A');
        var dur = 3200 + Math.random() * 1500;
        (function animateBlip() {
          blip.transition().duration(dur).ease(d3.easeLinear)
            .attrTween('cx', function () { return function (t) { return svgPointAt(line, t).x; }; })
            .attrTween('cy', function () { return function (t) { return svgPointAt(line, t).y; }; })
            .on('end', animateBlip);
        })();
      });

      cities.forEach(function (c) {
        var p = projection([c.lon, c.lat]);
        if (!p) return;
        var x = p[0], y = p[1];
        var grp = citiesLayer.append('g').attr('transform', 'translate(' + x + ',' + y + ')');
        if (c.hub) {
          grp.append('circle').attr('r', 9).attr('fill', 'none').attr('stroke', '#A6402A').attr('stroke-width', 1)
            .append('animate').attr('attributeName', 'r').attr('values', '9;16;9').attr('dur', '2.4s').attr('repeatCount', 'indefinite');
        }
        grp.append('circle').attr('r', c.hub ? 5 : 4).attr('fill', c.hub ? '#A6402A' : '#232E38');
        grp.append('text').attr('x', 8).attr('y', 4)
          .attr('font-family', "'Courier New',monospace").attr('font-size', '11px').attr('fill', '#3a2f22')
          .text(c.code);
      });
    }

    function routesForHub(cities) {
      var hub = cities.filter(function (c) { return c.hub; })[0];
      if (!hub) return [];
      return cities.filter(function (c) { return !c.hub; }).map(function (c) { return [hub.code, c.code]; });
    }

    var lastLiveKey = null;
    var MAX_LIVE_CITIES = 30;

    function uniqueCode(name, usedCodes) {
      var base = name.split(',')[0].trim().toUpperCase().replace(/[^A-Z]/g, '');
      for (var len = 3; len <= base.length; len++) {
        var code = base.slice(0, len);
        if (!usedCodes[code]) return code;
      }
      var code2 = base.slice(0, 3), n = 2;
      while (usedCodes[code2 + n]) n++;
      return code2 + n;
    }

    function applyLiveCities(rawNames) {
      var hubCoords = coordsFor(HUB_CITY);
      var seen = {};
      var usedCodes = { MMH: true };
      var found = [];
      rawNames.forEach(function (name) {
        var key = normCity(name);
        if (!key || seen[key] || key === normCity(HUB_CITY)) return;
        seen[key] = true;
        var c = coordsFor(name);
        if (!c) return;
        var code = uniqueCode(name, usedCodes);
        usedCodes[code] = true;
        found.push({ code: code, name: name, lat: c.lat, lon: c.lon });
      });
      if (!found.length) return;

      var cities = hubCoords
        ? [{ code: 'MMH', name: HUB_CITY, lat: hubCoords.lat, lon: hubCoords.lon, hub: true }].concat(found.slice(0, MAX_LIVE_CITIES))
        : found.slice(0, MAX_LIVE_CITIES + 1);

      var key = cities.map(function (c) { return c.code + '|' + c.name; }).join(',');
      if (key === lastLiveKey) return;
      lastLiveKey = key;
      plot(cities, routesForHub(cities));
    }

    function pollLiveStatus() {
      fetch('/pages/sat-live-status', { cache: 'no-store' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          if (!data) return;
          var names = [];
          if (data.city) names.push(data.city);
          if (Array.isArray(data.recent)) data.recent.forEach(function (c) { names.push(c); });
          if (names.length) applyLiveCities(names);
        })
        .catch(function () {});
    }

    fetch('https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json').then(function (r) { return r.json(); }).then(function (topology) {
      var states = topojson.feature(topology, topology.objects.states);
      g.insert('g', '*').selectAll('path').data(states.features).enter().append('path')
        .attr('d', path).attr('fill', '#c9b98e').attr('stroke', '#6B4A2E').attr('stroke-width', 0.7);

      plot(FALLBACK_CITIES, FALLBACK_ROUTES);

      pollLiveStatus();
      setInterval(pollLiveStatus, 6000);
    });

    return true;
  }

  function syncPortal() {
    placeholder = document.querySelector('[data-m="route-map-svg"]');
    var p = ensurePortal();

    if (!placeholder) { p.style.display = 'none'; return; }

    var r = placeholder.getBoundingClientRect();
    var visible = r.width > 0 && r.height > 0 && placeholder.offsetParent !== null;
    if (!visible) { p.style.display = 'none'; return; }

    if (!mapReady) { buildMap(p); }

    p.style.display = 'block';
    p.style.top = r.top + 'px';
    p.style.left = r.left + 'px';
    p.style.width = r.width + 'px';
    p.style.height = r.height + 'px';
  }

  setInterval(syncPortal, 400);
  window.addEventListener('scroll', syncPortal, { passive: true });
  window.addEventListener('resize', syncPortal);
  document.addEventListener('DOMContentLoaded', syncPortal);
  syncPortal();
})();
